import React from "react";
import ICalendarFactory from "../../planning/other/calendar/ICalendarFactory.tsx";
import Role from "../../../models/role.tsx";
import Ability from "../../../models/ability.tsx";
import UserModel from "../../../models/user.tsx";
import { DropDown } from "../../DropDown.tsx";

interface IUserModel extends UserModel {
    group: { name_groups: string };
}

interface UserProps {
    user: IUserModel;
    usersRoles: Array<{ role: Role, value: number }>;
    usersAbilities: Array<Ability>;

    t: (key: string) => string;
    handleDeleting: (user: UserProps["user"], options?: object) => void;
    handleModify: (user: UserProps["user"], options?: object) => void;
}


class User extends React.Component<UserProps> {
    render() {
        const {
            user,
            t,
            handleDeleting,
            handleModify,
            usersRoles,
            usersAbilities
        } = this.props;

        return (<div className="my-4 shrink-0 text-center border border-gray-200 shadow-md mx-2 flex overflow-hidden rounded-lg h-36" style={{ width: '26rem' }}>
            <div className="h-full flex flex-col w-1/2 bg-blue-400 pb-2 place-content-between">
                <div className="h-full flex justify-center">
                    <div className="flex justify-center rounded-full bg-white h-20 w-20 relative shadow-md my-2 overflow-hidden">
                        <img
                            src={"./svg/user.svg"}
                            className="absolute w-full h-full flex justify-center items-center p-1"
                            alt={`${user.firstname} ${user.lastname}`}
                        />
                    </div>
                </div>
                <div className="relative h-1 place-items-center flex text-center mb-4">
                    <div className="absolute w-full h-fit text-md flex flex-wrap text-white font-medium text-center place-content-center">
                        <p className="w-fit max-w-full h-fit truncate px-1">
                            {user.firstname} 
                        </p>
                        <p className="w-fit max-w-full h-fit truncate">
                            {user.lastname.toUpperCase()}
                        </p>
                    </div>
                </div>
            </div>

            <div className="h-full w-full p-2 pt-1">
                <div className="text-left">
                    <p className="text-md text-slate-500 text-left">
                        {ICalendarFactory.firstLetterUpperCase(user.group.name_groups)} - {user.language.toUpperCase()}
                    </p>
                    <p className="text-md text-slate-500 text-left ">{user.email}</p>

                    <div className="flex w-full justify-center my-2 space-x-2">

                        <DropDown
                            title={t("skills")}
                            list={usersRoles}
                            title_component={<>
                                <div className="m-px relative pt-px text-center w-fit flex bg-gray-100 border border-px border-gray-200 rounded ">
                                    <div className="w-fit px-1 text-xs"> {usersRoles.length} </div>
                                </div>
                            </>}
                            template={(element, index) => {
                                return (
                                    <div className="max-h-full px-2 py-0.5 m-1 text-sm rounded-lg border border-slate-300 flex  justify-center gap-x-1 grid grid-cols-8"
                                        style={{ 'backgroundColor': element.role.color + '80', }}
                                        key={index}
                                    >
                                        <div className="col-span-7 text-left pl-5"> {element.role.name} </div>
                                        <div className="border text-sm shadow w-fit min-w-5 pl-1.5 rounded-full" style={{ 'backgroundColor': ICalendarFactory.levelColor(element.value) }}>
                                            {element.value}
                                        </div>
                                    </div>
                                );
                            }}
                            placeholder={t("no skill")}
                        />

                        <DropDown
                            title={t("abilities")}
                            list={usersAbilities}
                            title_component={<>
                                <div className="m-px relative pt-px text-center w-fit flex bg-gray-100 border border-px border-gray-200 rounded ">
                                    <div className="w-fit px-1 text-xs"> {usersAbilities.length} </div>
                                </div>
                            </>}
                            template={(element, index) => {
                                return (
                                    <div
                                        className="max-h-full px-2 py-0.5 m-1 text-sm rounded-lg border border-slate-300 flex  justify-center gap-x-1 bg-blue-100"
                                        style={{ 'backgroundColor': element.color + '80' }}
                                        key={index}
                                    >
                                        <p className="text-center"> {element.name} </p>
                                    </div>
                                );
                            }}
                            placeholder={t("no abilities")}
                        />

                    </div>
                </div>



                <div className="flex gap-2 mt-1 w-full">
                    <button
                        type="button"
                        className="w-full ease-in-out duration-100 p-1.5 form-button-validate"
                        onClick={() => handleModify(user)}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#000000"
                        >
                            <path
                                id="Shape"
                                d="M.75,17.5A.751.751,0,0,1,0,16.75V12.569a.755.755,0,0,1,.22-.53L11.461.8a2.72,2.72,0,0,1,3.848,0L16.7,2.191a2.72,2.72,0,0,1,0,3.848L5.462,17.28a.747.747,0,0,1-.531.22ZM1.5,12.879V16h3.12l7.91-7.91L9.41,4.97ZM13.591,7.03l2.051-2.051a1.223,1.223,0,0,0,0-1.727L14.249,1.858a1.222,1.222,0,0,0-1.727,0L10.47,3.91Z"
                                transform="translate(5 3.25)"
                                fill="white"
                            ></path>
                        </svg>
                        <p className="pl-2">{t("modify")}</p>
                    </button>

                    <button
                        type="button"
                        className="w-full ease-in-out duration-100 p-1.5 form-button-delete"
                        onClick={() => handleDeleting(user)}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 12L14 16M14 12L10 16M18 6L17.1991 18.0129C17.129 19.065 17.0939 19.5911 16.8667 19.99C16.6666 20.3412 16.3648 20.6235 16.0011 20.7998C15.588 21 15.0607 21 14.0062 21H9.99377C8.93927 21 8.41202 21 7.99889 20.7998C7.63517 20.6235 7.33339 20.3412 7.13332 19.99C6.90607 19.5911 6.871 19.065 6.80086 18.0129L6 6M4 6H20M16 6L15.7294 5.18807C15.4671 4.40125 15.3359 4.00784 15.0927 3.71698C14.8779 3.46013 14.6021 3.26132 14.2905 3.13878C13.9376 3 13.523 3 12.6936 3H11.3064C10.477 3 10.0624 3 9.70951 3.13878C9.39792 3.26132 9.12208 3.46013 8.90729 3.71698C8.66405 4.00784 8.53292 4.40125 8.27064 5.18807L8 6"
                                stroke="white"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            ></path>
                        </svg>
                        <p className="pl-2">{t("delete")}</p>
                    </button>
                </div>
            </div>
        </div>);
    }
}
export default User;